// ============================================================================
// useReferralPingsUnread.js
//
// Live count of UNREAD ACTIVE Referral Pings for the current user, for the nav
// and dashboard badges. Mirrors useNotYetBadgeCount: two live collection-group
// listeners (recipient + sender), fail-safe to 0.
//
// Unread is computed client-side from the parent ping's denormalized activity +
// read fields (no message-subcollection scanning, no extra reads). Only ACTIVE
// pings (OPEN / ACKNOWLEDGED) count toward the numeric badge — RESOLVED/CANCELLED
// never inflate the active-attention number.
//
// NO PHI. Reads only non-PHI communication metadata. No new indexes.
// ============================================================================

import { useEffect, useState } from 'react';
import {
  subscribeToPingsForRecipient,
  subscribeToPingsForSender,
  isPingUnread,
  COMMUNICATION_STATUS,
} from '../core/index.js';

const ACTIVE = new Set([COMMUNICATION_STATUS.OPEN, COMMUNICATION_STATUS.ACKNOWLEDGED]);

/**
 * @param {{ tenantId: string|null, uid: string|null }} params
 * @returns {number} count of unread active pings for the user (0 when none/hidden)
 */
export function useReferralPingsUnread({ tenantId, uid }) {
  const [received, setReceived] = useState([]);
  const [sent, setSent] = useState([]);

  useEffect(() => {
    if (!tenantId || !uid) { setReceived([]); setSent([]); return; }
    const unsubR = subscribeToPingsForRecipient(tenantId, uid, {
      onData: (rows) => setReceived(rows),
      onError: (err) => {
        console.error('[useReferralPingsUnread] recipient listener error — count 0:', err?.code || err?.message);
        setReceived([]);
      },
    });
    const unsubS = subscribeToPingsForSender(tenantId, uid, {
      onData: (rows) => setSent(rows),
      onError: (err) => {
        console.error('[useReferralPingsUnread] sender listener error — count 0:', err?.code || err?.message);
        setSent([]);
      },
    });
    return () => { unsubR(); unsubS(); };
  }, [tenantId, uid]);

  // Dedupe by id (a ping is either sent or received, but guard anyway), then count
  // active + unread for this user.
  const byId = new Map();
  for (const p of [...received, ...sent]) byId.set(p.id, p);
  let count = 0;
  for (const p of byId.values()) {
    if (ACTIVE.has(p.status) && isPingUnread(p, uid)) count += 1;
  }
  return count;
}
